import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { UploadMetadata } from '../types/api';

export function useEntries(accountId?: string, limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['entries', accountId, limit, offset],
    queryFn: () => api.getEntries(accountId, limit, offset),
  });
}

export function useEntry(id: string) {
  return useQuery({
    queryKey: ['entry', id],
    queryFn: () => api.getEntry(id),
    enabled: !!id,
    refetchInterval: (query) => {
      const entry = query.state.data;
      // Poll every 3 seconds if transcription is processing
      if (entry?.transcriptionStatus === 'processing') {
        return 3000;
      }
      // Poll every 3 seconds if video is processing (has streamUid but not ready yet)
      if (entry?.mediaType === 'video' && entry.streamUid && !entry.streamReady) {
        return 3000;
      }
      return false;
    },
  });
}

export function useUploadAudio() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ blob, metadata }: { blob: Blob; metadata: UploadMetadata }) =>
      api.uploadAudio(blob, metadata),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });
}

export function useUpdateEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => api.updateEntry(id, title),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['entry', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });
}

export function useDeleteEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.deleteEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });
}

export function useRetranscribeEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.retranscribeEntry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['entry', id] });
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
  });
}

export function useSearchEntries(
  query: string,
  dateFrom?: number,
  dateTo?: number,
  limit = 50
) {
  return useQuery({
    queryKey: ['search', query, dateFrom, dateTo, limit],
    queryFn: () => api.searchEntries(query, dateFrom, dateTo, limit),
    enabled: query.length > 0,
  });
}

export function useSharedAccounts() {
  return useQuery({
    queryKey: ['shared-accounts'],
    queryFn: () => api.getSharedAccounts(),
  });
}
